import React, { useEffect, useState } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { FaWhatsapp, FaTrash, FaBaby, FaArrowLeft, FaUsers, FaClock } from 'react-icons/fa';

const CapacitySlot = ({ slot, reservas }) => {
    const journeyLimit = 7;

    const journeyCount = reservas.filter(r => 
        r.tipo_registro === 'Subscription' && 
        r.dia === slot.day && 
        r.horario === slot.time && 
        r.edad_rango === slot.age
    ).length;

    let badgeText = journeyCount >= journeyLimit ? "Fully Booked" : "Available";
    let badgeColor = journeyCount >= journeyLimit ? "#9c27b0" : "#90a178";
    let badgeBg = journeyCount >= journeyLimit ? "#f3e5f5" : "#f1f4ed";

    return (
        <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            padding: '16px', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
            border: '1px solid #f0f0f0',
            flex: '1 1 300px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#6b9da7', textTransform: 'uppercase', marginBottom: '2px' }}>{slot.age}</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#4A6B6B' }}>{slot.time}</div>
                </div>
                <span style={{ 
                    fontSize: '10px', 
                    fontWeight: 'bold', 
                    padding: '3px 8px', 
                    borderRadius: '12px',
                    color: badgeColor,
                    backgroundColor: badgeBg
                }}>
                    {badgeText}
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: '#888' }}>8-Week Journey spots:</span>
                    <span style={{ fontWeight: 'bold', color: journeyCount >= journeyLimit ? '#ffb300' : '#5d4037' }}>{journeyCount} / {journeyLimit}</span>
                </div>
                <div style={{ height: '6px', width: '100%', backgroundColor: '#f5f5f5', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ 
                        height: '100%', 
                        width: `${Math.min(100, (journeyCount / journeyLimit) * 100)}%`, 
                        backgroundColor: journeyCount >= journeyLimit ? '#ffb300' : '#6b9da7',
                        borderRadius: '3px'
                    }} />
                </div>
            </div>
        </div>
    );
};

const CapacityGrid = ({ day, baseSlots, reservas }) => {
    const daySlots = baseSlots.filter(s => s.day === day);

    return (
        <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
                fontFamily: 'Playfair Display, serif', 
                fontSize: '20px', 
                color: '#4A6B6B', 
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
            }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: day === 'Tuesday' ? '#6b9da7' : '#90a178' }}></span>
                {day} Sessions
            </h3>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                {daySlots.map((slot, idx) => (
                    <CapacitySlot key={idx} slot={slot} reservas={reservas} />
                ))}
            </div>
        </div>
    );
};

const parseDateToTimestamp = (dateVal) => {
    if (!dateVal) return 0;
    if (typeof dateVal === 'number') return dateVal;
    if (typeof dateVal?.toMillis === 'function') return dateVal.toMillis();
    if (dateVal?.seconds) return dateVal.seconds * 1000;
    
    if (typeof dateVal === 'string') {
        const str = dateVal.trim();
        if (!str) return 0;

        // 1. ISO format: YYYY-MM-DD...
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
            const parsed = Date.parse(str);
            if (!isNaN(parsed)) return parsed;
        }

        // 2. Clean string: remove words like 'klo', normalize spaces
        let cleanStr = str.replace(/\bklo\b/i, ' ').replace(/\s+/g, ' ');

        // Check for AM / PM indicators
        const isPM = /\b(pm|p\.m\.)\b/i.test(cleanStr);
        const isAM = /\b(am|a\.m\.)\b/i.test(cleanStr);
        const hasAMPM = isPM || isAM;

        // Match: part1 [separator] part2 [separator] year
        const dateMatch = cleanStr.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
        if (dateMatch) {
            const p1 = parseInt(dateMatch[1], 10);
            const p2 = parseInt(dateMatch[2], 10);
            const year = parseInt(dateMatch[3], 10);

            // Extract time parts (HH:mm[:ss])
            const timePart = cleanStr.substring(dateMatch[0].length);
            const timeMatch = timePart.match(/(\d{1,2})[:\.](\d{1,2})(?:[:\.](\d{1,2}))?/);
            let hour = 0, minute = 0, second = 0;
            if (timeMatch) {
                hour = parseInt(timeMatch[1], 10);
                minute = parseInt(timeMatch[2], 10);
                second = timeMatch[3] ? parseInt(timeMatch[3], 10) : 0;
            }

            if (hasAMPM) {
                if (isPM && hour < 12) hour += 12;
                if (isAM && hour === 12) hour = 0;
            }

            let day, month;

            if (p1 > 12) {
                // p1 is definitely Day, p2 is Month (e.g. 27/07/2026, 16/06/2026, 13/05/2026)
                day = p1;
                month = p2 - 1;
            } else if (p2 > 12) {
                // p2 is definitely Day, p1 is Month (e.g. 6/30/2026, 5/25/2026, 6/17/2026)
                month = p1 - 1;
                day = p2;
            } else if (hasAMPM && !cleanStr.includes('-') && !cleanStr.includes('.')) {
                // US standard format with AM/PM: Month/Day/Year (e.g. 5/8/2026, 10:20:26 AM; 5/11/2026, 5:18:38 PM)
                month = p1 - 1;
                day = p2;
            } else {
                // European / International format: Day/Month/Year (e.g. 7-9-2026, 07/09/2026, 2-9-2026, 5-8-2026, 03/07/2026)
                day = p1;
                month = p2 - 1;
            }

            const date = new Date(year, month, day, hour, minute, second);
            if (!isNaN(date.getTime())) return date.getTime();
        }

        const fallback = Date.parse(str);
        if (!isNaN(fallback)) return fallback;
    }
    return 0;
};

const getSortTime = (item) => {
    return (
        parseDateToTimestamp(item.timestamp) ||
        parseDateToTimestamp(item.createdAt) ||
        parseDateToTimestamp(item.created_at) ||
        parseDateToTimestamp(item.fecha_registro) ||
        parseDateToTimestamp(item.fecha) ||
        0
    );
};

const AdminPanel = () => {
    const [reservas, setReservas] = useState([]);
    const [espera, setEspera] = useState([]);
    const [auth, setAuth] = useState(false);
    const [password, setPassword] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [ageFilter, setAgeFilter] = useState('All');
    const [activeTab, setActiveTab] = useState('registrations'); 
    
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isPurging, setIsPurging] = useState(false);

    const baseSlots = [
        { day: 'Tuesday', time: '10:00 - 11:00', age: '0-12 months' },
        { day: 'Tuesday', time: '11:15 - 12:15', age: '1-3 years' },
        { day: 'Wednesday', time: '10:00 - 11:00', age: '1-3 years' },
        { day: 'Wednesday', time: '11:15 - 12:15', age: '0-12 months' },
    ];

    useEffect(() => {
        if (!auth) return;

        const unsubReservas = onSnapshot(collection(db, "reservas"), (snap) => {
            setReservas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubEspera = onSnapshot(collection(db, "lista_espera"), (snap) => {
            setEspera(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubReservas();
            unsubEspera();
        };
    }, [auth]);

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === 'blossom2026') {
            setAuth(true);
        } else {
            alert('Incorrect password');
        }
    };

    const handleDeleteReserva = async (id) => {
        try {
            await deleteDoc(doc(db, "reservas", id));
            setDeleteConfirmId(null);
        } catch (error) {
            console.error("Error deleting reservation:", error);
            alert("Could not delete the reservation.");
        }
    };

    const handlePurgeAll = async () => {
        try {
            const snap = await getDocs(collection(db, 'reservas'));
            const deletePromises = snap.docs.map(d => deleteDoc(d.ref));
            await Promise.all(deletePromises);
            setIsPurging(false);
            alert("All reservations have been successfully purged!");
        } catch (error) {
            console.error("Error purging test data:", error);
            alert("Error purging the data. Check console.");
        }
    };

    const getGlobalStats = () => {
        const journeyCount = reservas.filter(r => r.tipo_registro === 'Subscription').length;
        const trialCount = reservas.filter(r => r.tipo_registro === 'Complimentary Session').length;
        const waitlistCount = espera.length;
        return { journeyCount, trialCount, waitlistCount };
    };

    if (!auth) {
        return (
            <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f9f8f4' }}>
                <form onSubmit={handleLogin} style={{ padding: '40px', backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', textAlign: 'center', maxWidth: '400px', width: '90%' }}>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#6b9da7', marginBottom: '20px' }}>Admin Access</h2>
                    <input 
                        type="password" 
                        placeholder="Enter admin password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ width: '100%', padding: '12px 20px', marginBottom: '20px', borderRadius: '12px', border: '1px solid #d0d0ac', fontFamily: 'Lato, sans-serif' }}
                    />
                    <button type="submit" style={{ width: '100%', padding: '12px', backgroundColor: '#6b9da7', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight: 'bold', transition: '0.3s' }}>Login</button>
                </form>
            </div>
        );
    }

    return (
        <div style={{ padding: '40px', backgroundColor: '#f9f8f4', minHeight: '100vh', fontFamily: 'Lato, sans-serif', color: '#5d4037' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div>
                        <h1 style={{ fontFamily: 'Playfair Display, serif', margin: 0, fontSize: '28px', color: '#4A6B6B' }}>The Community Shelter</h1>
                        <p style={{ margin: 0, fontSize: '14px', color: '#6b9da7', fontStyle: 'italic' }}>Live Management Dashboard</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <button 
                        onClick={() => setActiveTab('registrations')}
                        style={{ padding: '10px 20px', borderRadius: '50px', border: 'none', cursor: 'pointer', backgroundColor: activeTab === 'registrations' ? '#6b9da7' : 'white', color: activeTab === 'registrations' ? 'white' : '#6b9da7', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                    >
                        <FaUsers style={{ marginRight: '8px' }} /> Registrations
                    </button>
                    <button 
                        onClick={() => setActiveTab('waitlist')}
                        style={{ padding: '10px 20px', borderRadius: '50px', border: 'none', cursor: 'pointer', backgroundColor: activeTab === 'waitlist' ? '#6b9da7' : 'white', color: activeTab === 'waitlist' ? 'white' : '#6b9da7', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                    >
                        <FaClock style={{ marginRight: '8px' }} /> Waitlist
                    </button>
                </div>
            </header>

            {/* Capacity Grid - The Pulse of the Sanctuary */}
            <div style={{ marginBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', margin: 0, fontSize: '22px' }}>Live Capacity Monitoring</h2>
                    <div style={{ display: 'flex', gap: '20px', fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#6b9da7' }}></span>
                            <span>8-Week Journey (x/7)</span>
                        </div>
                    </div>
                </div>
                
                <CapacityGrid day="Tuesday" baseSlots={baseSlots} reservas={reservas} />
                <CapacityGrid day="Wednesday" baseSlots={baseSlots} reservas={reservas} />
            </div>

            {activeTab === 'registrations' ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ fontFamily: 'Playfair Display, serif', margin: 0 }}>Community Members</h2>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <select 
                                value={filterType} 
                                onChange={(e) => setFilterType(e.target.value)}
                                style={{ padding: '8px 15px', borderRadius: '12px', border: '1px solid #d0d0ac', backgroundColor: 'white', color: '#4A6B6B', fontWeight: 'bold' }}
                            >
                                <option value="All">All Types</option>
                                <option value="Subscription">8-Week Journey</option>
                                <option value="Complimentary Session">Complimentary Session</option>
                            </select>

                            <select 
                                value={ageFilter} 
                                onChange={(e) => setAgeFilter(e.target.value)}
                                style={{ padding: '8px 15px', borderRadius: '12px', border: '1px solid #d0d0ac', backgroundColor: 'white', color: '#4A6B6B', fontWeight: 'bold' }}
                            >
                                <option value="All">All Ages</option>
                                <option value="0-12 months">0-12 months</option>
                                <option value="1-3 years">1-3 years</option>
                            </select>
                            
                            {isPurging ? (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#ffebee', padding: '5px 15px', borderRadius: '12px' }}>
                                    <span style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '13px' }}>Sure?</span>
                                    <button onClick={handlePurgeAll} style={{ padding: '5px 10px', backgroundColor: '#d32f2f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Purge</button>
                                    <button onClick={() => setIsPurging(false)} style={{ padding: '5px 10px', backgroundColor: '#9e9e9e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
                                </div>
                            ) : (
                                <button onClick={() => setIsPurging(true)} style={{ padding: '8px 15px', backgroundColor: '#fbe9e7', color: '#d32f2f', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                                    <FaTrash style={{ marginRight: '5px' }} /> Purge Data
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', background: 'white', borderRadius: '24px', boxShadow: '0 5px 20px rgba(0,0,0,0.02)', padding: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>THE BOND</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>SESSION INFO</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>TYPE</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>CONTACT</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px', textAlign: 'center' }}>ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reservas
                                    .filter(r => filterType === 'All' || r.tipo_registro === filterType)
                                    .filter(r => ageFilter === 'All' || r.edad_rango === ageFilter)
                                    .sort((a, b) => getSortTime(b) - getSortTime(a))
                                    .map(r => (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f9f9f9', transition: '0.2s' }}>
                                            <td style={{ padding: '20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ backgroundColor: '#E6E6FA', color: '#6b9da7', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <FaBaby />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{r.madre}</div>
                                                        <div style={{ fontSize: '13px', color: '#888' }}>& {r.bebe}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <div style={{ fontSize: '14px' }}>
                                                    <strong>{r.tipo_registro === 'Subscription' ? `Weekly (${r.dia})` : (r.fecha_clase || r.dia)}</strong><br/>
                                                    <span style={{ color: '#888' }}>{r.horario} • {r.edad_rango}</span>
                                                    {(r.fecha_registro || r.created_at) && (
                                                        <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                                                            Booked: {r.fecha_registro || new Date(r.created_at || r.timestamp).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <span style={{ 
                                                    padding: '5px 12px', 
                                                    borderRadius: '50px', 
                                                    fontSize: '11px', 
                                                    fontWeight: 'bold',
                                                    backgroundColor: r.tipo_registro === 'Subscription' ? '#e0f2f1' : '#f3e5f5',
                                                    color: r.tipo_registro === 'Subscription' ? '#00695c' : '#7b1fa2'
                                                }}>
                                                    {r.tipo_registro === 'Subscription' ? '8-WEEK JOURNEY' : 'COMPLIMENTARY'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <a 
                                                    href={`https://wa.me/${r.telefono?.replace(/\D/g, '')}`} 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    style={{ color: '#25D366', display: 'flex', alignItems: 'center', gap: '5px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px' }}
                                                >
                                                    <FaWhatsapp size={18} /> Chat
                                                </a>
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center' }}>
                                                {deleteConfirmId === r.id ? (
                                                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                        <button onClick={() => handleDeleteReserva(r.id)} style={{ padding: '5px 10px', backgroundColor: '#d32f2f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Yes</button>
                                                        <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '5px 10px', backgroundColor: '#9e9e9e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>No</button>
                                                    </div>
                                                ) : (
                                                    <button 
                                                        onClick={() => setDeleteConfirmId(r.id)}
                                                        style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#ffcdd2' }}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '20px' }}>Waitlist Connections</h2>
                    <div style={{ overflowX: 'auto', background: 'white', borderRadius: '24px', boxShadow: '0 5px 20px rgba(0,0,0,0.02)', padding: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>THE BOND</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>REQUESTED DAY</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px' }}>REGISTERED</th>
                                    <th style={{ padding: '20px', color: '#6b9da7', fontSize: '14px', textAlign: 'center' }}>CONTACT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {espera.length === 0 ? (
                                    <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>The waitlist is currently empty.</td></tr>
                                ) : (
                                    espera
                                        .slice()
                                        .sort((a, b) => getSortTime(b) - getSortTime(a))
                                        .map(e => (
                                        <tr key={e.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                            <td style={{ padding: '20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ backgroundColor: '#fff3cd', color: '#856404', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <FaClock />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{e.madre}</div>
                                                        <div style={{ fontSize: '13px', color: '#888' }}>& {e.bebe}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <span style={{ fontSize: '14px' }}>{e.dia}</span>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <span style={{ fontSize: '14px', color: '#888' }}>{e.fecha || 'N/A'}</span>
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center' }}>
                                                <a 
                                                    href={`https://wa.me/${e.telefono?.replace(/\D/g, '')}`} 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    style={{ color: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', textDecoration: 'none', fontWeight: 'bold' }}
                                                >
                                                    <FaWhatsapp size={18} /> WhatsApp
                                                </a>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                <a href="/" style={{ color: '#6b9da7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                    <FaArrowLeft /> Back to Sanctuary
                </a>
            </div>
        </div>
    );
};

export default AdminPanel;